import type {
  FieldValues,
  UseFormProps as UseHookFormProps,
} from 'react-hook-form'
import { type DefaultValues, useForm as useHookForm } from 'react-hook-form'
import type { z } from 'zod'

import type { ZodInfer } from '@mntn-dev/utility-types'

import type { Resolver } from '../resolver/types.ts'
import { zodInternalResolver } from '../resolver/zod.ts'

type AsyncDefaultValues<TFieldValues> = (
  payload?: unknown
) => Promise<TFieldValues>

type UseFormBaseProps<
  TFieldValues extends FieldValues,
  TContext,
  TResolvedValues extends FieldValues,
> = Omit<
  UseHookFormProps<TFieldValues, TContext, TResolvedValues>,
  'defaultValues' | 'resolver' | 'values'
> & {
  defaultValues?: DefaultValues<TFieldValues> | AsyncDefaultValues<TFieldValues>
  values?: TFieldValues
}

export type UseFormPropsWithResolver<
  TFieldValues extends FieldValues,
  TContext,
  TSchema extends z.ZodType<FieldValues>,
  TResolvedValues extends FieldValues = ZodInfer<TSchema>,
> = UseFormBaseProps<TFieldValues, TContext, TResolvedValues> & {
  resolver: Resolver<TSchema, TResolvedValues>
}

export type UseFormPropsWithoutResolver<
  TFieldValues extends FieldValues,
  TContext,
> = UseFormBaseProps<TFieldValues, TContext, TFieldValues> & {
  resolver?: undefined
}

export type UseFormProps<
  TFieldValues extends FieldValues,
  TContext = unknown,
  TSchema extends z.ZodType<FieldValues> = z.ZodType<FieldValues>,
  TResolvedValues extends FieldValues = ZodInfer<TSchema>,
> =
  | UseFormPropsWithResolver<TFieldValues, TContext, TSchema, TResolvedValues>
  | UseFormPropsWithoutResolver<TFieldValues, TContext>

export function useForm<
  TFieldValues extends FieldValues,
  TContext,
  TSchema extends z.ZodType<FieldValues>,
  TResolvedValues extends FieldValues = ZodInfer<TSchema>,
>(
  props: UseFormPropsWithResolver<
    TFieldValues,
    TContext,
    TSchema,
    TResolvedValues
  >
): ReturnType<typeof useHookForm<TFieldValues, TContext, TResolvedValues>>
export function useForm<TFieldValues extends FieldValues, TContext = unknown>(
  props: UseFormPropsWithoutResolver<TFieldValues, TContext>
): ReturnType<typeof useHookForm<TFieldValues, TContext, TFieldValues>>
export function useForm<
  TFieldValues extends FieldValues,
  TContext,
  TSchema extends z.ZodType<FieldValues>,
  TResolvedValues extends FieldValues = ZodInfer<TSchema>,
>(
  props:
    | UseFormPropsWithResolver<TFieldValues, TContext, TSchema, TResolvedValues>
    | UseFormPropsWithoutResolver<TFieldValues, TContext>
) {
  let resolver:
    | ReturnType<
        typeof zodInternalResolver<
          TFieldValues,
          TContext,
          TSchema,
          TResolvedValues
        >
      >
    | undefined

  if (hasResolver<TFieldValues, TContext, TSchema, TResolvedValues>(props)) {
    resolver = zodInternalResolver<
      TFieldValues,
      TContext,
      TSchema,
      TResolvedValues
    >(
      props.resolver.schema,
      props.resolver.tFunction,
      props.resolver.schemaOptions,
      props.resolver.resolverOptions
    )
  }

  const hookForm = useHookForm<TFieldValues, TContext, TResolvedValues>({
    ...(props as UseFormBaseProps<TFieldValues, TContext, TResolvedValues>),
    resolver,
  })

  return hookForm
}

function hasResolver<
  TFieldValues extends FieldValues,
  TContext,
  TSchema extends z.ZodType<FieldValues>,
  TResolvedValues extends FieldValues,
>(
  props:
    | UseFormPropsWithResolver<TFieldValues, TContext, TSchema, TResolvedValues>
    | UseFormPropsWithoutResolver<TFieldValues, TContext>
): props is UseFormPropsWithResolver<
  TFieldValues,
  TContext,
  TSchema,
  TResolvedValues
> {
  return typeof props.resolver !== 'undefined'
}
